/**
 * Realtime Zod Schemas
 *
 * Zod validation schemas for realtime transport types.
 *
 * @module schemas/realtime
 * @see SPEC-007 Requirements 1.6, 3.5
 */
import { z } from 'zod';
import { PresenceDataSchema } from './presence.zod';
/**
 * Room token payload schema
 * @see Requirement 1.6
 */
export const RoomTokenPayloadSchema = z.object({
    userId: z.string().min(1),
    docId: z.string().min(1),
    role: z.enum(['owner', 'editor', 'viewer']),
    tenantId: z.string().min(1),
    exp: z.number().int().positive(),
    iat: z.number().int().positive(),
    jti: z.string().min(1),
});
/**
 * Token response schema
 * @see Requirement 1.6
 */
export const TokenResponseSchema = z.object({
    token: z.string().min(1),
    expiresAt: z.number().int().positive(),
    room: z.string().regex(/^doc:.+$/, 'Room must be in format doc:{docId}'),
    presenceChannel: z
        .string()
        .regex(/^presence:.+$/, 'Presence channel must be in format presence:{docId}'),
    realtimeBase: z.string().url('realtimeBase must be a valid URL'),
});
/**
 * Token request schema (for POST /realtime/token)
 */
export const TokenRequestSchema = z.object({
    docId: z.string().min(1).max(100),
});
/**
 * Realtime message schemas
 * @see Requirement 3.5
 */
export const PresenceMessageSchema = z.object({
    type: z.literal('presence'),
    data: PresenceDataSchema,
});
export const JoinMessageSchema = z.object({
    type: z.literal('join'),
    userId: z.string().min(1),
    presence: PresenceDataSchema,
});
export const LeaveMessageSchema = z.object({
    type: z.literal('leave'),
    userId: z.string().min(1),
});
export const SyncMessageSchema = z.object({
    type: z.literal('sync'),
    data: z.unknown(),
});
export const PingMessageSchema = z.object({
    type: z.literal('ping'),
});
export const PongMessageSchema = z.object({
    type: z.literal('pong'),
    rtt: z.number().nonnegative(),
});
/**
 * Union schema for all realtime message types
 */
export const RealtimeMessageSchema = z.discriminatedUnion('type', [
    PresenceMessageSchema,
    JoinMessageSchema,
    LeaveMessageSchema,
    SyncMessageSchema,
    PingMessageSchema,
    PongMessageSchema,
]);
/**
 * Connection state schema
 */
export const ConnectionStateSchema = z.enum([
    'disconnected',
    'connecting',
    'connected',
    'reconnecting',
    'error',
]);
/**
 * Connection metrics schema
 */
export const ConnectionMetricsSchema = z.object({
    state: ConnectionStateSchema,
    reconnectCount: z.number().int().nonnegative(),
    connectedDurationMs: z.number().nonnegative().nullable(),
    lastRtt: z.number().nonnegative().nullable(),
});
/**
 * Room metadata schema
 */
export const RoomMetadataSchema = z.object({
    docId: z.string().min(1),
    createdAt: z.number().int().positive(),
    participantCount: z.number().int().nonnegative(),
});
/**
 * Realtime error type schema
 * @see SPEC-007 Requirements 2.6, 9.6 - Tenant Isolation
 */
export const RealtimeErrorTypeSchema = z.enum([
    'token_fetch_failed',
    'token_expired',
    'token_invalid',
    'unauthorized',
    'room_full',
    'connection_failed',
    'connection_lost',
    'server_error',
    'tenant_mismatch',
]);
//# sourceMappingURL=realtime.zod.js.map